import type { LOCATION_SEARCH_TYPE } from '@/types'

export type AutocompleteGroupHeader = { isGroupHeader: true; itemName: string }
export type AutocompleteListItem = LOCATION_SEARCH_TYPE | AutocompleteGroupHeader

export type AutocompleteDataResult = {
  autocompleteData: AutocompleteListItem[]
  listType: string | undefined
}

export type BuildEmptyInputAutocompleteResultParams = {
  mergeRecentAndRecommendedSearches: boolean
  recentSearches: LOCATION_SEARCH_TYPE[]
  recommendedSearches: LOCATION_SEARCH_TYPE[]
}

export type BuildAutocompleteDataAndListTypeParams = {
  autocompleteValue: string | null
  mergeRecentAndRecommendedSearches: boolean
  recentSearches: LOCATION_SEARCH_TYPE[]
  recommendedSearches: LOCATION_SEARCH_TYPE[]
  typeAheadItems: LOCATION_SEARCH_TYPE[]
}

export function getLocationSearchKey(item: LOCATION_SEARCH_TYPE): string {
  return String(item.id ?? item.cityID ?? '')
}

/** Prefer account/API recents, then fill from persisted search data (same order as the dropdown: recents always above recommendations). */
export function mergeRecentSearchesById(
  accountRecents: LOCATION_SEARCH_TYPE[],
  persistedRecents: LOCATION_SEARCH_TYPE[],
  maxCount: number,
): LOCATION_SEARCH_TYPE[] {
  const seen = new Set<string>()
  const out: LOCATION_SEARCH_TYPE[] = []
  for (const source of [accountRecents, persistedRecents]) {
    for (const item of source) {
      const key = getLocationSearchKey(item)
      if (key === '' || seen.has(key)) continue
      seen.add(key)
      out.push(item)
      if (out.length >= maxCount) {
        return out
      }
    }
  }
  return out
}

/** Drop recommended cities that already appear in recents so the UI and analytics stay unambiguous. */
export function dedupeRecommendationsAgainstRecents(
  recentSearches: LOCATION_SEARCH_TYPE[],
  recommendedSearches: LOCATION_SEARCH_TYPE[],
): LOCATION_SEARCH_TYPE[] {
  const recentKeys = new Set(recentSearches.map(getLocationSearchKey).filter((key) => key !== ''))
  return recommendedSearches.filter((item) => {
    const key = getLocationSearchKey(item)
    return key === '' || !recentKeys.has(key)
  })
}

export function resolveClickListTypeForItem(
  selectedItem: LOCATION_SEARCH_TYPE,
  listType: string | undefined,
  recentSearches: LOCATION_SEARCH_TYPE[],
  recommendedSearches: LOCATION_SEARCH_TYPE[],
): string | undefined {
  if (listType !== 'recent_and_recommended') {
    return listType
  }

  const selectedKey = getLocationSearchKey(selectedItem)
  if (selectedKey === '') {
    return listType
  }

  if (recentSearches.some((item) => getLocationSearchKey(item) === selectedKey)) {
    return 'recent_searches'
  }

  const recommendationsExcludingRecents = dedupeRecommendationsAgainstRecents(
    recentSearches,
    recommendedSearches,
  )
  if (recommendationsExcludingRecents.some((item) => getLocationSearchKey(item) === selectedKey)) {
    return 'recommended'
  }

  return listType
}

export function isEmptyAutocompleteInput(value: string | null): boolean {
  return value === null || value === ''
}

export function hasRecentSearches(recentSearches: LOCATION_SEARCH_TYPE[]): boolean {
  return recentSearches.length > 0
}

export function hasRecommendedSearches(recommendedSearches: LOCATION_SEARCH_TYPE[]): boolean {
  return recommendedSearches.length > 0
}

export function getEmptyInputListType(hasRecent: boolean, hasRecommended: boolean): string {
  if (hasRecent && hasRecommended) {
    return 'recent_and_recommended'
  }
  if (hasRecommended) {
    return 'recommended'
  }
  return 'recent_searches'
}

function buildMergedRecentAndRecommendedRows(
  recentSearches: LOCATION_SEARCH_TYPE[],
  recommendedSearches: LOCATION_SEARCH_TYPE[],
): AutocompleteListItem[] {
  const staticRows: AutocompleteListItem[] = []
  if (hasRecentSearches(recentSearches)) {
    staticRows.push({ isGroupHeader: true, itemName: 'Recent Searches' }, ...recentSearches)
  }
  if (hasRecommendedSearches(recommendedSearches)) {
    staticRows.push({ isGroupHeader: true, itemName: 'Recommended Searches' }, ...recommendedSearches)
  }
  return staticRows
}

function buildMergedRecentAndRecommendedResult(
  recentSearches: LOCATION_SEARCH_TYPE[],
  recommendedSearches: LOCATION_SEARCH_TYPE[],
): AutocompleteDataResult | null {
  const recommendationsExcludingRecents = dedupeRecommendationsAgainstRecents(
    recentSearches,
    recommendedSearches,
  )
  const staticRows = buildMergedRecentAndRecommendedRows(recentSearches, recommendationsExcludingRecents)
  if (staticRows.length === 0) {
    return null
  }

  return {
    autocompleteData: staticRows,
    listType: getEmptyInputListType(
      hasRecentSearches(recentSearches),
      hasRecommendedSearches(recommendationsExcludingRecents),
    ),
  }
}

function buildControlRecentOrRecommendedResult(
  recentSearches: LOCATION_SEARCH_TYPE[],
  recommendedSearches: LOCATION_SEARCH_TYPE[],
): AutocompleteDataResult | null {
  const showRecommended = hasRecommendedSearches(recommendedSearches)
  const itemsToShow = showRecommended ? recommendedSearches : recentSearches
  if (itemsToShow.length === 0) {
    return null
  }

  return {
    autocompleteData: [
      {
        isGroupHeader: true,
        itemName: showRecommended ? 'Recommended Searches' : 'Recent Searches',
      },
      ...itemsToShow,
    ],
    listType: showRecommended ? 'recommended' : 'recent_searches',
  }
}

export function buildEmptyInputAutocompleteResult({
  mergeRecentAndRecommendedSearches,
  recentSearches,
  recommendedSearches,
}: BuildEmptyInputAutocompleteResultParams): AutocompleteDataResult | null {
  if (mergeRecentAndRecommendedSearches) {
    return buildMergedRecentAndRecommendedResult(recentSearches, recommendedSearches)
  }
  return buildControlRecentOrRecommendedResult(recentSearches, recommendedSearches)
}

export function buildAutocompleteDataAndListType({
  autocompleteValue,
  mergeRecentAndRecommendedSearches,
  recentSearches,
  recommendedSearches,
  typeAheadItems,
}: BuildAutocompleteDataAndListTypeParams): AutocompleteDataResult {
  if (isEmptyAutocompleteInput(autocompleteValue)) {
    const emptyInputResult = buildEmptyInputAutocompleteResult({
      mergeRecentAndRecommendedSearches,
      recentSearches,
      recommendedSearches,
    })
    if (emptyInputResult) {
      return emptyInputResult
    }
  }

  if (typeAheadItems.length > 0) {
    return { autocompleteData: typeAheadItems, listType: 'typein' }
  }

  return { autocompleteData: [], listType: undefined }
}
